// A long-running iPad WebContent process accumulates graphics memory WebKit does not
// release. A logout-triggered reload while the app is parked and untouched frees part
// of it (about 45-85 of ~170 MiB in the 2026-09-27 rig run); only an app restart frees
// the rest.
import { Injectable, NgZone, inject } from "@angular/core";
import { AlertController } from "@ionic/angular";

import { AuthService } from "../auth/auth.service";
import { FlightTimerService } from "../flight-timer/flight-timer.service";
import { GeolocationService } from "../geolocation/geolocation.service";
import { LogbookService } from "../logbook/logbook.service";
import { opsControlService } from "../ops-control/ops-control.service";
import { UpdatesService } from "../updates/updates.service";

const LOGIN_AGE_MS = 18 * 60 * 60 * 1000;
const IDLE_MS = 2 * 60 * 60 * 1000;
const STATIONARY_MS = 2 * 60 * 60 * 1000;
const LANDED_MS = 2 * 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;
const MOVING_SPEED_MPS = 2;
// How long the countdown dialog runs before the logout, and how often it re-checks.
const COUNTDOWN_MS = 60 * 60 * 1000;
const COUNTDOWN_TICK_MS = 1000;

/** "MM:SS" for the countdown dialog message, e.g. 3600000 -> "60:00". */
function formatCountdown(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** The countdown dialog message, shared by its initial text and every tick's update. */
function countdownMessage(remainingMs: number): string {
  return `Logging out in ${formatCountdown(remainingMs)}. Touch anywhere to cancel.`;
}

export function shouldLogout(s: {
  now: number;
  loginAt: number;
  lastInteractionAt: number;
  lastMovingAt: number;
  landedAt: number;
}): boolean {
  return (
    s.loginAt > 0 &&
    s.now - s.loginAt >= LOGIN_AGE_MS &&
    s.now - s.lastInteractionAt >= IDLE_MS &&
    s.now - s.lastMovingAt >= STATIONARY_MS &&
    (s.landedAt === 0 || s.now - s.landedAt >= LANDED_MS)
  );
}

@Injectable({ providedIn: "root" })
export class IdleReloadService {
  private geolocation = inject(GeolocationService);
  private zone = inject(NgZone);
  private auth = inject(AuthService);
  private flightTimer = inject(FlightTimerService);
  private updates = inject(UpdatesService);
  private opsControl = inject(opsControlService);
  private logbook = inject(LogbookService);
  private alertController = inject(AlertController);

  private lastInteractionAt = 0;
  private lastMovingAt = 0;
  private lastTimerRunningAt = 0;
  private started = false;

  private countdownInProgress = false;
  private countdownAlert: HTMLIonAlertElement | undefined;
  private countdownEndsAt = 0;
  private countdownTimer: ReturnType<typeof setInterval> | undefined;

  public reload: () => void = () => location.reload();

  private loginAt(): number {
    return this.auth.getLoginInformation_device()?.timestamp ?? 0;
  }

  /** State a logout would lose: a running flight timer (app start resets one older
   *  than 3 h), a half-installed update, a board-book write in progress, or
   *  memory-only state (a fuel burn not yet applied, a route recompute not yet done,
   *  board-book ops not yet sent). A hold that never clears only stops the logout,
   *  which is the state before this service existed. */
  private busy(): boolean {
    return (
      this.flightTimer.isTimerRunning() ||
      this.updates.isExecutingUpdate ||
      this.opsControl.unappliedFuelBurnKg > 0 ||
      this.opsControl.routeRecomputePending ||
      this.logbook.writesInFlight > 0 ||
      this.logbook.unsentSyncOps > 0
    );
  }

  /** The last landing. reset() zeroes the timer's landingTime right after a normal
   *  landing, so the last time the timer was seen running counts as a landing too. A
   *  running timer is therefore a landing now, which also keeps the idle windows closed. */
  private landedAt(): number {
    if (this.flightTimer.isTimerRunning()) {
      this.lastTimerRunningAt = Date.now();
    }
    return Math.max(this.flightTimer.flightTime?.landingTime ?? 0, this.lastTimerRunningAt);
  }

  /** Every idle/stationary/landed window holds. A touch, motion or a running timer ends
   *  it, so this cancels a running countdown. */
  private idleAndParked(): boolean {
    return shouldLogout({
      now: Date.now(),
      loginAt: this.loginAt(),
      lastInteractionAt: this.lastInteractionAt,
      lastMovingAt: this.lastMovingAt,
      landedAt: this.landedAt(),
    });
  }

  private canLogoutNow(): boolean {
    // idleAndParked() first: busy() short-circuits on a running timer, and landedAt()
    // must see that timer to record it.
    return this.idleAndParked() && !this.busy();
  }

  /** Opens the countdown dialog, unless one is already running. */
  private startCountdown(): void {
    if (this.countdownInProgress) {
      return;
    }
    this.countdownInProgress = true;
    void this.runCountdown();
  }

  private async runCountdown(): Promise<void> {
    try {
      this.countdownAlert = await this.alertController.create({
        header: "Automatic logout",
        message: countdownMessage(COUNTDOWN_MS),
        backdropDismiss: false,
      });
      await this.countdownAlert.present();
    } catch (err) {
      console.error("IdleReloadService: countdown dialog failed", err);
      this.cancelCountdown();
      return;
    }

    // The countdown runs from when the dialog is actually up, not from the check
    // that triggered it -- the two can be a promise turn apart.
    this.countdownEndsAt = Date.now() + COUNTDOWN_MS;
    this.countdownTimer = setInterval(() => {
      // A touch, motion or a running timer cancels the countdown at once.
      if (!this.idleAndParked()) {
        this.cancelCountdown();
        return;
      }
      const remainingMs = this.countdownEndsAt - Date.now();
      if (this.countdownAlert) {
        this.countdownAlert.message = countdownMessage(Math.max(0, remainingMs));
      }
      // A busy() hold only defers the logout: the update check runs every minute, so a
      // hold that cancelled would cancel every countdown.
      if (remainingMs <= 0 && !this.busy()) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = undefined;
        void this.finishCountdown();
      }
    }, COUNTDOWN_TICK_MS);
  }

  /** Cancels a running countdown: stops the timer, dismisses the dialog, and clears all
   *  state so a later tick can start a fresh countdown. Never discards or logs out. */
  private cancelCountdown(): void {
    clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;
    const alert = this.countdownAlert;
    this.countdownAlert = undefined;
    this.countdownEndsAt = 0;
    this.countdownInProgress = false;
    void alert?.dismiss();
  }

  /** Dismisses the dialog, then logs out and reloads. Like the manual logout it does
   *  not discard the ad-hoc duty: a discard syncs to every device on the registration,
   *  and this guard sees only this iPad, so it could clear the crew of a flying aircraft
   *  from an idle spare (Igor, 2026-09-28). */
  private async finishCountdown(): Promise<void> {
    const alert = this.countdownAlert;
    this.countdownAlert = undefined;
    this.countdownEndsAt = 0;
    try {
      await alert?.dismiss();
      // A touch or a new hold during the dismiss still stops it.
      if (!this.canLogoutNow()) {
        return;
      }
      await this.auth.logout_user();
      this.reload();
    } catch (err) {
      // The next check starts a new countdown.
      console.error("IdleReloadService: automatic logout failed", err);
    } finally {
      this.countdownInProgress = false;
    }
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const now = Date.now();
    this.lastInteractionAt = this.lastMovingAt = now;

    this.geolocation.geolocation$.subscribe((info) => {
      if (info.speed != null && info.speed > MOVING_SPEED_MPS) {
        this.lastMovingAt = Date.now();
      }
    });

    this.zone.runOutsideAngular(() => {
      for (const type of ["pointerdown", "keydown"]) {
        document.addEventListener(
          type,
          () => {
            this.lastInteractionAt = Date.now();
          },
          { passive: true }
        );
      }
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          this.lastInteractionAt = Date.now();
        }
      });

      setInterval(() => {
        if (this.canLogoutNow()) {
          this.startCountdown();
        }
      }, CHECK_INTERVAL_MS);
    });
  }
}
